import React, { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import styles from './../PatientManualRegistration.module.scss';
import FormControl from '../../../../../Components/Form/FormControl';
import Button from '../../../../../Components/Button/Button';
import Text from '../../../../../Components/Base/Text/Text';
import FormContainer from '../../../../../Components/Form/FormContainer';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import PhoneNumberField from '../../../../../Components/PhoneNumber/PhoneNumberField';
import { Moment } from 'moment';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CitizenshipSelectField from '../../../../Base/Components/Fields/Citizenship/CitizenshipSelectField';
import apiClient from '../../../../../Services/ApiClient';
import InputField from '../../../../../Components/Form/components/InputField';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import { Theme } from '../../../../../Enums/Theme.enum';
import useNotification from '../../../../../Hooks/useNotification.hook';
import Loading from '../../../../../Components/Loading';
import {
	TermItem,
	useWorkplaceCaseTargetQuery,
	useWorkplacePrivateQuery,
} from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import usePatient from '../../../../Patient/Hooks/usePatient';
import usePatientManualRegistrationCzechFormValidationScheme from '../../../Validation/PatientManualRegistrationCzechFormValidationScheme';
import PatientRegistrationHealthPlanSelect from '../Utils/PatientRegistrationHealthPlanSelect';
import HealthInsuranceCompanySelect from '../../../../Term/Components/HealthInsuranceCompanySelect/HealthInsuranceCompanySelect';
import GenderSelectField from '../../../../Base/Components/Inputs/GenderSelect/GenderSelectField';
import usePatientRegistration from '../../../Hooks/usePatientRegistration';
import PatientRegistrationFavoritePresetSelect, {
	createdByTypeOptions,
} from '../Utils/PatientRegistrationFavoritePresetSelect';
import useTranslation from '../../../../../Hooks/useTranslation';
import { CreatedByType } from '../../../Enums/CreatedByType.enum';
import { useAppContext } from '../../../../../Services/AppContext';

export interface PatientManualRegistrationCzechFormValues {
	insuranceNumber: string;
	salutationBefore?: string;
	salutationAfter?: string;
	firstName: string;
	lastName: string;
	email: string;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	healthInsuranceCompany?: SelectValue;
	citizenship: SelectValue;
	birthDate?: Moment;
	gender?: SelectValue;
	phoneNumber?: string;
	sendPatientInviteToMeddi: boolean;
	healthPlanCode?: SelectValue;
	healthPlanWorkplaceMember?: {
		value?: string;
		id?: string;
		label: string;
		item: any;
	};
	legalRepresentative?: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber?: string;
	};
	createdByType: SelectValue;
	createdById?: {
		id?: string;
		label: string;
		item: {
			user: {
				id: string;
			};
		};
	};
}

interface Props {
	onSuccess: (
		values: PatientManualRegistrationCzechFormValues,
		withHealthPlan: boolean,
	) => void;
}

const PatientManualRegistrationCzechForm: React.FC<Props> = ({ onSuccess }) => {
	const { t } = useTranslation();
	const { showNotificationAlert } = useNotification();
	const [isLoading, setIsLoading] = useState(false);
	const [isHealthPlanSelectReady, setIsHealthPlanSelectReady] = useState(true);

	const [isAllowToCheckWithCSSZAndVZP, setIsAllowToCheckWithCSSZAndVZP] =
		useState(false);
	const [isChild, setIsChild] = useState(false);
	const [citizenshipOptions, setCitizenshipOptions] = useState<
		TermItem[] | undefined
	>();
	const { checkIsAdultByInsuranceNumber } = usePatient();
	const { validationSchema } =
		usePatientManualRegistrationCzechFormValidationScheme(isChild);
	const { workplaceId } = useAppContext();
	const { data: WorkplaceCaseTargetData } = useWorkplaceCaseTargetQuery({
		id: workplaceId,
	});
	const { data: WorkplacePrivateData } = useWorkplacePrivateQuery({
		id: workplaceId,
	});
	const defaultValues: any = {
		sendPatientInviteToMeddi: true,
		createdByType: {
			label: t(
				'component.patientRegistrationFavoritePresetSelect.createdByTypeOptions.onMe',
			),
			value: CreatedByType.ME,
		},
	};

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const isPrivateWorkplace: boolean = useMemo(() => {
		return WorkplacePrivateData?.workplace?.privateAmbulance || false;
	}, [WorkplacePrivateData]);

	const {
		isGenderRequested,
		isBirthDateRequested,
		insuranceNumberValue,
		healthPlanCodeValue,
	} = usePatientRegistration({
		formMethods,
		setIsChild,
		citizenshipOptions,
		isPrivateWorkplace,
	});

	const handleLoadDefaultDataByBirthNumber = async () => {
		await handleLoadDataFromCSSZAndVZP();
	};

	const handleLoadDataFromCSSZAndVZP = async () => {
		setIsLoading(true);

		try {
			const { data: csszData }: any = await apiClient.get(
				'/cssz/' + insuranceNumberValue.replace('/', '') + '/trvala-adresa',
			);

			if (csszData?.address) {
				formMethods.setValue('address', csszData.address);
			}
			if (csszData?.patient) {
				formMethods.setValue('firstName', csszData.patient.firstName);
				formMethods.setValue('lastName', csszData.patient.lastName);
			}
		} catch (e: any) {
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}

		try {
			const { data: vzpData }: any = await apiClient.get(
				'/vzp/' + insuranceNumberValue.replace('/', '') + '/pojisteni',
			);

			if (vzpData?.insuranceCompany) {
				formMethods.setValue('healthInsuranceCompany', {
					value: vzpData.insuranceCompany?.id,
					label: `${vzpData.insuranceCompany?.value} - ${vzpData.insuranceCompany?.value2}`,
				});
			}
		} catch (e: any) {
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}

		setIsLoading(false);
	};

	const preselectCreatedByType = () => {
		const option = createdByTypeOptions.find(
			item =>
				item.value === WorkplaceCaseTargetData?.workplace?.createCaseTarget,
		);
		if (option) {
			formMethods.setValue('createdByType', {
				label: t(option.label),
				value: option.value,
			});
		}
	};

	const handleSuccess = (values: any) => {
		if (!healthPlanCodeValue || healthPlanCodeValue === 'withoutPlan') {
			values.healthPlanWorkplaceMember = undefined;
		}
		onSuccess(values as PatientManualRegistrationCzechFormValues, false);
	};

	const handleSuccessWithPlan = (values: any) => {
		onSuccess(values as PatientManualRegistrationCzechFormValues, true);
	};

	useEffectAsync(async () => {
		setIsAllowToCheckWithCSSZAndVZP(insuranceNumberValue?.length >= 9);
		if (insuranceNumberValue && insuranceNumberValue?.length >= 6) {
			setIsChild(
				!checkIsAdultByInsuranceNumber(
					insuranceNumberValue,
					isPrivateWorkplace,
				),
			);
		}
		if (insuranceNumberValue?.length < 6) {
			setIsChild(false);
		}
		//if (insuranceNumberValue && insuranceNumberValue?.length >= 9) {
		//const validationResult = await validateBirthNumber(insuranceNumberValue);
		//if (!validationResult) {
		//	showNotificationAlert({
		//		content: t(
		//			'component.patientManualRegistrationCzech.nonValidInsuranceNumber',
		//		),
		//	});
		//}
		//}
	}, [insuranceNumberValue, isPrivateWorkplace]);

	useEffect(() => {
		preselectCreatedByType();
	}, [WorkplaceCaseTargetData]);

	return (
		<FormWrapper formMethods={formMethods}>
			<div className='v2-forms__gray-background'>
				<div className='row'>
					<div className='col-lg-4 col-12'>
						<InputField
							name='insuranceNumber'
							label='base.shared.insuranceNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-8 col-12'>
						<Text
							tx='base.verificationOfNamePermanentResidenceAddressAndInsuranceValidity'
							className={styles.hintText}
						/>
						{isLoading ? (
							<Loading />
						) : (
							<Button
								title='base.automaticallyFillInAndVerifyDataViaCSSAAndVZP'
								onClick={handleLoadDefaultDataByBirthNumber}
								disabled={!isAllowToCheckWithCSSZAndVZP}
								theme={
									isAllowToCheckWithCSSZAndVZP ? Theme.BLUE : Theme.DISABLED
								}
							/>
						)}
					</div>
				</div>
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationBefore'
							label='base.shared.salutationBefore'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationAfter'
							label='base.shared.salutationAfter'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
				</div>
			)}
			<div className='row'>
				<div className='col-lg-6 col-12'>
					<InputField
						name='firstName'
						label='base.shared.firstName'
						isRequired
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<InputField name='lastName' label='base.shared.lastName' isRequired />
				</div>
			</div>
			<Text className={styles.formText} tx='base.permanentAddress' firstUpper />
			<FormContainer name={'address'}>
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField name='street' label='base.shared.street' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='descriptiveNumber'
							label='base.shared.descriptiveNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-3 col-12'>
						<InputField name='guideNumber' label='base.shared.guideNumber' />
					</div>
				</div>
				<div className='row'>
					<div className='col-lg-6 col-12'>
						<InputField name='city' label='base.shared.city' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='postCode'
							label='base.shared.postCode'
							isRequired
						/>
					</div>
				</div>
			</FormContainer>
			<Text
				className={styles.formText}
				tx='base.additionalInformations'
				firstUpper
			/>
			<FormControl
				name='healthInsuranceCompany'
				label='base.healthInsuranceCompany'>
				<HealthInsuranceCompanySelect />
			</FormControl>
			<div className='row mb-3'>
				<div className='col-lg-6 col-12'>
					<CitizenshipSelectField
						name='citizenship'
						label='base.citizenship'
						isRequired
						onLoad={setCitizenshipOptions}
					/>
				</div>
				{isBirthDateRequested && (
					<div className='col-lg-6 col-12'>
						<FormControl
							name='birthDate'
							label='base.shared.dateOfBirth'
							isRequired>
							<DatePicker />
						</FormControl>
					</div>
				)}
				{isGenderRequested && (
					<div className='col-lg-6 col-12'>
						<GenderSelectField
							valueKey='id'
							name='gender'
							label='base.shared.gender'
							isRequired
						/>
					</div>
				)}
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<PhoneNumberField name='phoneNumber' label='base.shared.phone' />
					</div>
					<div className='col-lg-6 col-12'>
						<InputField name='email' label='base.shared.email' isRequired />
					</div>
				</div>
			)}
			<div className='mb-3'>
				{isHealthPlanSelectReady && (
					<PatientRegistrationHealthPlanSelect formMethods={formMethods} />
				)}
			</div>
			<div className='mb-3'>
				<PatientRegistrationFavoritePresetSelect />
			</div>
			{isChild && (
				<>
					<Text
						className={styles.formText}
						tx='base.shared.legalRepresentative'
						firstUpper
					/>
					<FormContainer name={'legalRepresentative'}>
						<div className='row mb-3'>
							<div className='col-lg-6 col-12'>
								<InputField
									name='firstName'
									label='base.shared.firstName'
									isRequired
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField
									name='lastName'
									label='base.shared.lastName'
									isRequired
								/>
							</div>
						</div>
						<div className='row'>
							<div className='col-lg-6 col-12'>
								<PhoneNumberField
									name='phoneNumber'
									label='base.shared.phone'
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField name='email' label='base.shared.email' isRequired />
							</div>
						</div>
					</FormContainer>
				</>
			)}
			<div className='v2-forms__gray-background footer mt-3'>
				<div
					className='d-flex flex-row align-items-center'
					style={{ gap: '10px' }}>
					<div>
						<SubmitButton
							onSubmit={handleSuccess}
							title='base.registerThePatient'
						/>
					</div>
					{healthPlanCodeValue && healthPlanCodeValue !== 'withoutPlan' && (
						<div>
							<SubmitButton
								onSubmit={handleSuccessWithPlan}
								title='component.patientManualRegistrationComponent.registerAndSetupHealthPlan'
							/>
						</div>
					)}
					<div className='pl-3' style={{ width: '350px' }}>
						<Text
							tx='base.emailWithInvitationWillArriveToPatientsEmail'
							className='v2-forms__light-text'
							firstUpper
						/>
						<CheckboxField
							name='sendPatientInviteToMeddi'
							tx='base.sendPatientInviteToMeddi'
							isRequired
							className='mt-2'
						/>
					</div>
				</div>
			</div>
		</FormWrapper>
	);
};

export default PatientManualRegistrationCzechForm;
